
import { Component, namespace, Vue } from 'nuxt-property-decorator'
import type { MetaInfo } from 'vue-meta'
import OfferPageFaq from '~/components/offer-details/OfferPageFaq.vue'
import BlackAprilJudithBanner from '~/components/banners/BlackAprilJudithBanner.vue'
import supportIcon from '~/assets/images/support.png'
import chatCirclesIcon from '~/assets/images/icons/icon-chats-circle.svg'
import PhoneNumberLink from '~/components/snippets/PhoneNumberLink.vue'
import IconPhone from '~/assets/icons/icon-phone-filled.svg?inline'
import IconArrowSimple from '~/assets/images/icons/icon-arrow-simple-right.svg?inline'
import IconPaperPlane from '~/assets/images/icons/icon-paper-plane-rounded.svg?inline'
import {
  ActionType as QuizActionType,
  GetterType as QuizGetterType,
  namespace as quizNamespace,
  PublicAction as QuizPublicAction,
  PublicGetter as QuizPublicGetter,
} from '~/store/modules/Quiz'
import { OnCreated, OnMounted, WithHead } from '~/core/vue.types'
import { AlButton } from '~/components/ui'
import { getExternalCompareMobileHref } from '~/utils/external-links.utils'
import { FAQItem, useSEOMetaComposer } from '~/composables/seo.composable'
import { FeatureFlags } from '~/core/config/feature-flags'

const QuizStore = namespace(quizNamespace)

@Component({
  components: {
    AlButton,
    OfferPageFaq,
    BlackAprilJudithBanner,
    PhoneNumberLink,
    IconPhone,
    IconArrowSimple,
    IconPaperPlane,
  },
})
export default class GetAdvicePage extends Vue implements WithHead, OnCreated, OnMounted {
  @QuizStore.Getter(QuizGetterType.ADVICE_CODE)
  private readonly adviceCode!: QuizPublicGetter[QuizGetterType.ADVICE_CODE]

  @QuizStore.Getter(QuizGetterType.ANSWER_ROUTE_ACTION)
  private readonly answerRouteAction!: QuizPublicGetter[QuizGetterType.ANSWER_ROUTE_ACTION]

  @QuizStore.Action(QuizActionType.INIT)
  private readonly initState!: QuizPublicAction[QuizActionType.INIT]

  protected phoneSupportIcon = Object.freeze(supportIcon)
  protected chatSupportIcon = Object.freeze(chatCirclesIcon)

  readonly featureNewVoucherProcess = this.$featureFlags.isEnabled(FeatureFlags.NEW_VOUCHER_PROCESS)

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.$t('head.meta.getAdvice.title').toString(),
        description: this.$t('head.meta.getAdvice.description').toString(),
        route: { name: 'get-advice' },
        faq: {
          items: this.faqItems,
        },
      },
    )
  }

  get faqItems () {
    return [
      { question: this.$t('faq.header1'), answer: this.$t('faq.content1') },
      { question: this.$t('faq.header2'), answer: this.$t('faq.content2') },
      { question: this.$t('faq.header3'), answer: this.$t('faq.content3') },
      this.featureNewVoucherProcess && { question: this.$t('faq.header4'), answer: this.$t('faq.content4') },
      { question: this.$t('faq.header5'), answer: this.$t('faq.content5') },
      { question: this.$t('faq.header6'), answer: this.$t('faq.content6') },
      { question: this.$t('faq.header7'), answer: this.$t('faq.content7') },
    ].filter(Boolean) as FAQItem[]
  }

  get compareRoute () {
    return this.localePath(this.answerRouteAction?.name || 'index')
  }

  get externalCompareMobileHref (): string {
    return getExternalCompareMobileHref(this.$config.BASE_URL, this.localePath)
  }

  async created () {
    await this.initState()

    if (process.client) {
      if (!this.adviceCode) {
        await this.$router.replace(this.externalCompareMobileHref)
      }
    }
  }

  mounted () {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Quiz',
    })
  }

  openChat () {
    // TODO: Handle Dixa chat opening.
    // We used to use a Hubspot chat, but now we are using Dixa.
    // const chatToggleButton: HTMLButtonElement | null = document.querySelector('.hubspot-inline-chat-toggle')
    // if (chatToggleButton) {
    //   chatToggleButton.click()
    // }
  }
}
